import { Injectable, signal } from '@angular/core';

/**
 * Holds the current anymatch session id + pin for the exhibitor edit flow.
 *
 * The edit link is `…/public/stop-form?s=<sessionId>&a=<stopId>&pin=<pin>`. These
 * credentials are sent as `X-Session-Id` / `X-Session-Pin` headers on every public
 * API call (see {@link SessionPinInterceptor}) — the lightweight access barrier
 * guarding the pin-less public routes.
 */
@Injectable({ providedIn: 'root' })
export class SessionCredentialsService {
  private readonly sessionId = signal<string | null>(null);
  private readonly pin = signal<string | null>(null);

  /** Capture session id + pin from the edit-link query params. Idempotent. */
  captureFromUrl(): void {
    if (this.sessionId() && this.pin()) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const s = params.get('s');
    const pin = params.get('pin');
    if (s) {
      this.sessionId.set(s);
    }
    if (pin) {
      this.pin.set(pin);
    }
  }

  set(sessionId: string | null, pin: string | null): void {
    if (sessionId) {
      this.sessionId.set(sessionId);
    }
    if (pin) {
      this.pin.set(pin);
    }
  }

  /**
   * Exhibitors arrive through the pin edit link and use the pin-guarded public routes;
   * signed-in organizers have no pin and use the private routes, which check their team.
   */
  get apiScope(): 'public' | 'private' {
    this.captureFromUrl();
    return this.pin() ? 'public' : 'private';
  }

  get id(): string | null {
    return this.sessionId();
  }

  get sessionPin(): string | null {
    return this.pin();
  }
}
